import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormContext } from "react-hook-form";
import { Button, Col, Row } from "reactstrap";
import { message, Popover } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import FormTextArea from "../../form-components/TextAreaComponent";
import usersApis from "../../../api/master/users";
import dropdownApis from "../../../api/WorkOrderManagement/DropdownApis";
import { fetchSequenceAccess } from "../../../store/slices/misc/sequenceAccess";
import Swal from "sweetalert2";
import WorkBOQApprovalDrawer from "./WorkBOQApprovalDrawer";

interface SelectOptions {
  label: string;
  value: string;
}

const WorkBoqGeneralDetails: React.FC = (props: any) => {
  const { data: sequenceAccess } = useSelector(
    (state: any) => state.sequenceAccess
  );
  const dispatch: any = useDispatch();
  const location = useLocation();

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  // Get flowType from URL
  const searchParams = new URLSearchParams(location.search);
  const flowType = searchParams.get("flowType");
  const isClientFlow = flowType?.toLowerCase() === "client";

  const [sitesOptions, setSitesOptions] = useState<SelectOptions[]>([]);
  const [projectOptions, setProjectOptions] = useState<SelectOptions[]>([]);
  const [contractorOptions, setContractorOptions] = useState<SelectOptions[]>(
    []
  );
  const [materialBoqOptions, setMaterialBoqOptions] = useState<SelectOptions[]>(
    []
  );
  const [budgetOptions, setBudgetOptions] = useState<SelectOptions[]>([]);
  const [approvals, setApprovals] = useState({
    open: false,
    data: [],
  });
  const [isStrictApproval, setIsStrictApproval] = useState<boolean>(false);
  const [isFormDataFetched, setIsFormDataFetched] = useState(false);

  const siteIdWatch = watch("siteId");
  const workLevelWatch = watch("workLevel");
  const approvalRequiredWatch = watch("approvalRequired");
  const AllFormDetails = watch();

  const withSelected = (
    opts: SelectOptions[],
    idField: string,
    labelField: string
  ): SelectOptions[] => {
    const id = AllFormDetails?.[idField];
    const idStr = id ? String(id) : "";
    if (!idStr || opts.some((o) => o.value === idStr)) return opts;
    return [{ label: AllFormDetails?.[labelField] || idStr, value: idStr }, ...opts];
  };

  const mergedProjectOptions = withSelected(projectOptions, "projectId", "projectLabel");
  const mergedContractorOptions = withSelected(contractorOptions, "contractorId", "contractorLabel");
  const mergedBudgetOptions = withSelected(budgetOptions, "budgetId", "budgetLabel");
  const mergedMaterialBoqOptions = withSelected(materialBoqOptions, "materialBoqId", "materialBoqLabel");

  // Completion alert percentage options (5% intervals)
  const completionAlertOptions = Array.from({ length: 20 }, (_, i) => {
    const value = (i + 1) * 5;
    return { label: `${value}%`, value: value.toString() };
  });

  useEffect(() => {
    dispatch(fetchSequenceAccess());
    fetchSites();
  }, [dispatch]);

  useEffect(() => {
    if (siteIdWatch) {
      fetchProjectsForSite(siteIdWatch);
      fetchContractorsForSite(siteIdWatch);
      fetchMaterialBoqOptions(siteIdWatch);
      fetchBudgetOptions(siteIdWatch);
      fetchSegmentLevel(siteIdWatch);
    } else {
      setProjectOptions([]);
      setContractorOptions([]);
      setMaterialBoqOptions([]);
      setBudgetOptions([]);
      setValue("workLevel", "");
    }
  }, [siteIdWatch]);

  const fetchSegmentLevel = async (siteId: string) => {
    try {
      const { data, status } = await dropdownApis.fetchSegmentLevelBySiteId(
        parseInt(siteId)
      );
      if (status && data) {
        const workLevel = data || "Site";
        let displayWorkLevel = workLevel;
        if (workLevel === "Tower" || workLevel === "Block") {
          displayWorkLevel = "Tower / Block";
        }

        setValue("workLevel", displayWorkLevel);
        setValue("segmentLevel", workLevel); 
      } else {
        setValue("workLevel", "Site");
        setValue("segmentLevel", "Site");
      }
    } catch (error) {
      setValue("workLevel", "Site");
      setValue("segmentLevel", "Site");
    }
  };

  const fetchSites = async () => {
    try {
      const data = await usersApis.getCurrentUser();
      if (data) {
        const sitesOptions = data?.siteAccessibility?.map((item: any) => ({
          label: item.siteName,
          value: item.siteID.toString(),
        }));
        setSitesOptions(sitesOptions);
      }
    } catch (error) {
      console.log("Error fetching sites:", error);
    }
  };

  const fetchProjectsForSite = async (siteId: string) => {
    try {
      const { data, status } = await dropdownApis.getProjectsDropdown(
        parseInt(siteId)
      );
      if (status && data) {
        const projectOptions = data.map((item: any) => ({
          label: item?.projectTitle,
          value: item?.projectId?.toString(),
        }));
        setProjectOptions(projectOptions);
      }
    } catch (error) {
      console.log("Error fetching projects:", error);
      message.error("Failed to fetch projects for selected site");
    }
  };

  const fetchContractorsForSite = async (siteId: string) => {
    try {
      // Get flowType from URL to determine partyType
      const currentSearchParams = new URLSearchParams(location.search);
      const currentFlowType = currentSearchParams.get("flowType");
      const isClient = currentFlowType?.toLowerCase() === "client";

      // Determine partyType based on flowType
      // If client flow, pass "Client", otherwise pass "Contractor"
      const partyType = isClient ? "Client" : "Contractor";

      const { data, status } =
        await dropdownApis.fetchContractorsBySiteIdDropdown({
          id: siteId.toString(),
          partyType: partyType,
        });
      if (status && data) {
        const contractorOptions = data.map((item: any) => ({
          label: item?.companyName || item?.name,
          value: item?.contractorID?.toString() || item?.id?.toString(),
        }));
        setContractorOptions(contractorOptions);
      }
    } catch (error) {
      console.log("Error fetching contractors:", error);
      message.error("Failed to fetch contractors for selected site");
    }
  };

  const fetchMaterialBoqOptions = async (siteId?: string) => {
    try {
      if (siteId) {
        const { data, status } = await dropdownApis.materialBOQDropDown(
          parseInt(siteId)
        );
        if (status && data) {
          const materialBoqOptions = data.map((item: any) => ({
            label: item?.targetNumber,
            value: item?.targetId?.toString(),
          }));
          setMaterialBoqOptions(materialBoqOptions);
        }
      } else {
        setMaterialBoqOptions([]);
      }
    } catch (error) {
      console.log("Error fetching material BOQs:", error);
      message.error("Failed to fetch material BOQs");
    }
  };

  const fetchBudgetOptions = async (siteId: string) => {
    try {
      const response = await dropdownApis.getBudgetDropdownBySiteId(
        parseInt(siteId)
      );
      if (response?.status && response?.data) {
        const budgetOpts = response.data.map((budget: any) => ({
          label: budget.budgetName,
          value: budget.headerId?.toString(),
        }));
        setBudgetOptions(budgetOpts);
      }
    } catch (error) {
      console.log("Error fetching budget options:", error);
      message.error("Failed to fetch budget options for selected site");
    }
  };

  const validateForm = (data: any) => {
    if (!data.boqTitle?.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "BOQ Title is required",
      });
      return false;
    }

    if (data.boqTitle.length > 100) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "BOQ Title cannot exceed 100 characters",
      });
      return false;
    }

    if (!data.siteId) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Site Selection is required",
      });
      return false;
    }

    // if (!data.materialBoqId) {
    //   Swal.fire({
    //     icon: "error",
    //     title: "Validation Error",
    //     text: "Material BOQ Selection is required",
    //   });
    //   return false;
    // }

    if (!data.workLevel) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Work Level Settings is required",
      });
      return false;
    }

    // Approval Required validation
    if (!data.approvalRequired) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please specify if approval is required",
      });
      return false;
    }

    return true;
  };

  const setFormValues = () => {
    const { approvalsData } = AllFormDetails;

    if (approvalsData) {
      try {
        const _approvals = JSON.parse(approvalsData);
        console.log("Setting form approvals data:", _approvals);
        setApprovals(_approvals);
      } catch (error) {
        console.error("Error parsing approvals data in setFormValues:", error);
        setApprovals({ open: false, data: [] });
      }
    }
  };

  useEffect(() => {
    if (!isFormDataFetched && Object.keys(AllFormDetails).length > 0) {
      setFormValues();
      setIsFormDataFetched(true);
    }
  }, [AllFormDetails]);

  const submitForm = async (data: any) => {
    if (!validateForm(data)) return;
    // Set approvals data in form
    setValue("approvalsData", JSON.stringify(approvals));
    setValue("isStrictApproval", isStrictApproval.toString());

    // Only block navigation if approval is required ("true") but no approvals set
    if (data.approvalRequired === "true" && approvals?.data?.length === 0) {
      Swal.fire({
        icon: "error",
        title: "Approval Required",
        text: "Please set the required approvals before proceeding to the next step.",
      });
      return;
    }

    let apiWorkLevel = data.workLevel;
    if (data.workLevel === "Tower / Block") {
      apiWorkLevel = "Tower";
    }
    const payload = {
      ...data,
      workLevel: data.workLevel,
      segmentLevel: apiWorkLevel,
      isStrictApproval: Boolean(isStrictApproval),
      approvalsData: JSON.stringify(approvals),
      step: "generalDetails",
    };

    console.log("General Details Payload:", payload);
    props.next(payload);
  };

  return (
    <>
      <form className="content clearfix" onSubmit={handleSubmit(submitForm)}>
        <div className="card">
          <div className="card-body">
            <Row className="gy-4">
              <Col lg="6">
                <FormInput
                  name="boqTitle"
                  label="BOQ Title"
                  required={true}
                  placeholder="Enter BOQ Title"
                  maxLength={100}
                  tooltipText="Title or Name for the BOQ"
                />
              </Col>
              <Col lg="6">
                <FormSelect
                  name="siteId"
                  label="Site Selection"
                  options={sitesOptions}
                  required={true}
                  placeholder="Select Site"
                  tooltipText="Select from available construction sites"
                  onChange={(value) => {
                    const selectedSite = sitesOptions.find(
                      (option) => option.value === value
                    );
                    setValue("siteLabel", selectedSite?.label || "");
                  }}
                />
              </Col>

              <Col lg="12">
                <FormTextArea
                  name="boqDescription"
                  label="BOQ Description"
                  placeholder="Enter detailed description of the BOQ (Max 500 characters)"
                  maxLength={500}
                  rows={3}
                // tooltipText="Detailed description of the BOQ"
                />
              </Col>

              <Col lg="6">
                <FormSelect
                  name="projectId"
                  label="Project Selection"
                  options={mergedProjectOptions}
                  placeholder="Select Project"
                  tooltipText="Projects assigned to the current user"
                  onChange={(value) => {
                    const selectedProject = projectOptions.find(
                      (option) => option.value === value
                    );
                    setValue("projectLabel", selectedProject?.label || "");
                  }}
                />
              </Col>
              <Col lg="6">
                <FormSelect
                  name="contractorId"
                  label={
                    isClientFlow ? "Client Selection" : "Contractor Selection"
                  }
                  options={mergedContractorOptions}
                  placeholder={
                    isClientFlow ? "Select Client" : "Select Contractor"
                  }
                  tooltipText={
                    isClientFlow
                      ? "Clients onboarded in the system"
                      : "Contractors onboarded in the system"
                  }
                  onChange={(value) => {
                    const selectedContractor = contractorOptions.find(
                      (option) => option.value === value
                    );
                    setValue(
                      "contractorLabel",
                      selectedContractor?.label || ""
                    );
                  }}
                />
              </Col>

              <Col lg="6">
                <FormSelect
                  name="budgetId"
                  label="Budget Selection"
                  options={mergedBudgetOptions}
                  required={true}
                  placeholder="Select Budget"
                  tooltipText="Budget lines available for the selected site"
                  onChange={(value) => {
                    const selectedBudget = mergedBudgetOptions.find(
                      (option) => option.value === value
                    );
                    setValue("budgetLabel", selectedBudget?.label || "");
                  }}
                />
              </Col>

              <Col lg="6">
                <FormSelect
                  name="completionAlertsFrom"
                  label="Completion Alerts From"
                  options={completionAlertOptions}
                  placeholder="Select Alert Percentage"
                  tooltipText="Alert level based on completion percentage (5%, 10%, etc.)"
                />
              </Col>
              <Col lg="6">
                <FormSelect
                  name="materialBoqId"
                  label="Select Material BOQ"
                  options={mergedMaterialBoqOptions}
                  required={false}
                  placeholder="Select Material BOQ"
                  tooltipText="Link to existing Material BOQs"
                  onChange={(value) => {
                    const selectedMaterialBoq = mergedMaterialBoqOptions.find(
                      (option) => option.value === value
                    );
                    setValue(
                      "materialBoqLabel",
                      selectedMaterialBoq?.label || ""
                    );
                  }}
                />
              </Col>

              <Col lg="6">
                <FormInput
                  name="workLevel"
                  label="Work Level Settings"
                  required={true}
                  disabled={true}
                  placeholder="Auto-filled based on site selection"
                  tooltipText="Work level is determined by the selected site configuration"
                />
              </Col>

              {/* Approval Required - Mandatory Boolean */}
              <Col lg="6">
                <FormSelect
                  name="approvalRequired"
                  label="Approval Required ?"
                  options={[
                    { label: "Yes", value: "true" },
                    { label: "No", value: "false" },
                  ]}
                  placeholder="Select"
                  disabled={!siteIdWatch}
                  required={true}
                  tooltipText="If Yes, configure approval hierarchy as per reference path"
                />

                {/* Hidden field for approvals data */}
                <div style={{ display: "none" }}>
                  <FormInput name="approvalsData" label="" type="text" />
                </div>

                {/* Set Approvals Link */}
                {approvalRequiredWatch === "true" && (
                  <Popover
                    content="Click here to set required approvals"
                    title="Approval Required"
                  >
                    <a
                      href="#"
                      className="success"
                      onClick={(e) => {
                        e.preventDefault();
                        if (!siteIdWatch) {
                          message.error("Please select a site first");
                          return;
                        }
                        setApprovals((pre) => {
                          return {
                            ...pre,
                            open: true,
                          };
                        });
                      }}
                    >
                      <EditOutlined /> Set Approvals &nbsp;
                      {approvals?.data?.length === 0 ? (
                        <span className="mandatory">Please set approvals</span>
                      ) : (
                        <span className="text-success">
                          ✓ {approvals.data.length} approval(s) configured
                        </span>
                      )}
                    </a>
                  </Popover>
                )}
              </Col>
            </Row>
          </div>
        </div>

        <div className="actions clearfix">
          <ul>
            <li>
              <Button color="primary" type="submit">
                Next
              </Button>
            </li>
          </ul>
        </div>
      </form>

      {/* Work Order Approval Drawer */}
      {approvals.open && (
        <WorkBOQApprovalDrawer
          open={approvals.open}
          siteId={parseInt(siteIdWatch) || 0}
          onClose={() => {
            setApprovals((pre) => {
              return {
                ...pre,
                open: false,
              };
            });
          }}
          onSubmit={(data: any, isStrictApprovalFromDrawer: boolean) => {
            setApprovals({
              open: false,
              data: data,
            });
            setIsStrictApproval(isStrictApprovalFromDrawer);
          }}
          initialStrictApproval={isStrictApproval}
          setApprovalSubmissionStatus={(status: boolean) => { }}
          approvalData={approvals.data}
        />
      )}
    </>
  );
};

export default WorkBoqGeneralDetails;
